import { CommonModule } from '@angular/common';
import { MaskService } from '../../../../../shared/services/mask.service';
import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxCurrencyDirective } from 'ngx-currency';
import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { ContasPagarService } from '../../../../../shared/services/apis/financas/contas-pagar.service';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { TipoParcelamento } from '../../../../../shared/services/models/financas/tipo-parcelamento.model';
import { ContaFinanceira } from '../../../../../shared/services/models/financas/conta-financeira.model';
import { FormaPagamento } from '../../../../../shared/services/models/financas/forma-pagamento.model';

export interface RenegociacaoModalData {
    idContasPagar: number;
    valorEmAberto: number;
    valorTotal: number;
    descricaoDocumento: string;
    listaTiposParcelamento: TipoParcelamento[];
    listaContaFinanceira: ContaFinanceira[];
    listaFormaPagamento: FormaPagamento[];
    currencyMask: any;
}

@Component({
    selector: 'app-modal-renegociacao-parcelas',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatIconModule,
        MatProgressSpinnerModule,
        NgxCurrencyDirective,
        SelectPesquisavelComponent
    ],
    templateUrl: './modal-renegociacao-parcelas.component.html',
    styleUrl: './modal-renegociacao-parcelas.component.css'
})
export class ModalRenegociacaoParcelasComponent implements OnInit {

    public form: FormGroup;
    public isSimulando: boolean = false;
    public isSalvando: boolean = false;
    public simulado: boolean = false;
    public integerMask: any;

    get parcelasArray(): FormArray {
        return this.form.get('parcelas') as FormArray;
    }

    get idFormaPagamentoControl(): any { return this.form.get('idFormaPagamento'); }
    get idContaFinanceiraControl(): any { return this.form.get('idContaFinanceira'); }

    get tipoParcelamentoSelecionado(): TipoParcelamento | null {
        const id = this.form.get('idTipoParcelamento')?.value;
        if (!id) return null;
        return this.data.listaTiposParcelamento.find(t => t.idTipoParcelamento === id) ?? null;
    }

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: RenegociacaoModalData,
        public dialogRef: MatDialogRef<ModalRenegociacaoParcelasComponent>,
        private fb: FormBuilder,
        private contasPagarService: ContasPagarService,
        private alertService: AlertService,
        private maskService: MaskService
    ) {
        this.integerMask = this.maskService.getIntegerMask();
        this.form = this.fb.group({
            idTipoParcelamento: [null],
            dataVencimento: [''],
            intervaloDiasParcelas: [30],
            idFormaPagamento: [null],
            idContaFinanceira: [null],
            parcelas: this.fb.array([])
        });
    }

    ngOnInit(): void {
        // Reset simulação ao trocar tipo parcelamento
        this.form.get('idTipoParcelamento')?.valueChanges.subscribe(() => {
            this.parcelasArray.clear();
            this.simulado = false;
        });
    }

    public simular(): void {
        const idTipoParcelamento = this.form.get('idTipoParcelamento')?.value;
        const dataVencimento = this.form.get('dataVencimento')?.value;
        const intervaloDiasParcelas = this.form.get('intervaloDiasParcelas')?.value;

        if (!idTipoParcelamento) {
            this.alertService.show('Atenção', 'Selecione o tipo de parcelamento.', AlertType.warning);
            return;
        }
        if (!dataVencimento) {
            this.alertService.show('Atenção', 'Informe a data de vencimento inicial.', AlertType.warning);
            return;
        }

        const request = {
            valorTotal: this.data.valorEmAberto,
            idTipoParcelamento,
            dataVencimento: new Date(dataVencimento).toISOString(),
            intervaloDiasParcelas: intervaloDiasParcelas || 30,
            descricaoBase: this.data.descricaoDocumento
        };

        this.isSimulando = true;
        this.contasPagarService.simularParcelas(request).subscribe({
            next: (res: any) => {
                this.isSimulando = false;
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription || 'Erro ao simular parcelas.', AlertType.error);
                    return;
                }
                this.popularGrid(res.parcelas || []);
                this.simulado = true;
            },
            error: () => {
                this.isSimulando = false;
                this.alertService.show('Erro', 'Erro ao comunicar-se com o servidor.', AlertType.error);
            }
        });
    }

    private popularGrid(parcelas: any[]): void {
        this.parcelasArray.clear();
        parcelas.forEach((p: any) => {
            this.parcelasArray.push(this.fb.group({
                nroParcela: [p.nroParcela],
                dataVencimento: [p.dataVencimento ? new Date(p.dataVencimento) : null],
                valor: [p.valor],
                percentual: [p.percentual],
                descricao: [p.descricao]
            }));
        });
    }

    public onParcelaValorChange(index: number): void {
        const ctrl = this.parcelasArray.at(index);
        const valor = ctrl.get('valor')?.value || 0;
        const perc = this.data.valorEmAberto > 0 ? Math.round((valor / this.data.valorEmAberto) * 10000) / 100 : 0;
        ctrl.get('percentual')?.setValue(perc, { emitEvent: false });
    }

    public confirmar(): void {
        if (!this.simulado || this.parcelasArray.length === 0) {
            this.alertService.show('Atenção', 'É necessário simular as parcelas antes de confirmar.', AlertType.warning);
            return;
        }

        const parcelas = this.parcelasArray.controls.map((ctrl: any) => ({
            nroParcela: ctrl.get('nroParcela')?.value,
            dataVencimento: ctrl.get('dataVencimento')?.value ? new Date(ctrl.get('dataVencimento').value).toISOString() : null,
            valor: ctrl.get('valor')?.value || 0,
            descricao: ctrl.get('descricao')?.value,
            idFormaPagamento: this.form.get('idFormaPagamento')?.value,
            idContaFinanceira: this.form.get('idContaFinanceira')?.value
        }));

        const request = {
            idContasPagar: this.data.idContasPagar,
            idTipoParcelamento: this.form.get('idTipoParcelamento')?.value,
            dataVencimento: new Date(this.form.get('dataVencimento')?.value).toISOString(),
            intervaloDiasParcelas: this.form.get('intervaloDiasParcelas')?.value,
            idFormaPagamento: this.form.get('idFormaPagamento')?.value,
            idContaFinanceira: this.form.get('idContaFinanceira')?.value,
            parcelas
        };

        this.isSalvando = true;
        this.contasPagarService.renegociarParcelas(request).subscribe({
            next: (res: any) => {
                this.isSalvando = false;
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription || 'Erro ao renegociar parcelas.', AlertType.error);
                    return;
                }
                this.alertService.show('Sucesso', 'Parcelas renegociadas com sucesso!', AlertType.success);
                this.dialogRef.close({ success: true });
            },
            error: () => {
                this.isSalvando = false;
                this.alertService.show('Erro', 'Erro ao comunicar-se com o servidor.', AlertType.error);
            }
        });
    }

    public cancelar(): void {
        this.dialogRef.close({ success: false });
    }


  onDateInput(event: Event): void {
    MaskService.onDateInput(event);
  }
}
